import { useJobillaOrgId } from '@/hooks'
import { emitUnhandledError, ExportCenter, logger } from '@/ui'
import { useExportIntegrationIndex } from '@jobilla/nexus-sdk'
// eslint-disable-next-line import/no-internal-modules
import { exportIntegrationDelete } from '@jobilla/nexus-sdk/lib/axios'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute(
	'/_auth/company/$companyId/settings/export',
)({
	component: RouteComponent,
})

function RouteComponent() {
	const jobillaOrgId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const [isWaitingForWebhook, setIsWaitingForWebhook] = useState(false)
	const [isDisconnecting, setIsDisconnecting] = useState(false)

	const { data, isLoading } = useExportIntegrationIndex(
		{
			'filter[organisation_id]': jobillaOrgId,
		},
		{
			refetchInterval: isWaitingForWebhook ? 3000 : false,
		},
	)

	const handleConnectionComplete = () => {
		setIsWaitingForWebhook(true)
		setTimeout(() => setIsWaitingForWebhook(false), 30 * 1000)
	}

	const handleDisconnect = () => {
		const integrationId = data?.[0]?.id
		if (!integrationId) {
			emitUnhandledError({
				title: 'Failed to disconnect integration',
				description: 'No active integration found. Please refresh and try again.',
				cause: new Error('Missing integrationId'),
			})
			return
		}

		const companyId = jobillaOrgId
		const queryKey = [{ url: '/export-integrations' }]

		setIsDisconnecting(true)

		exportIntegrationDelete(integrationId)
			.then(() => {
				logger.track('Export Center: Disconnected Integration', {
					tags: { category: 'export-center' },
					data: { action: 'disconnect-integration', integrationId, companyId },
				})
			})
			.catch((error) => {
				logger.error(error, {
					tags: { category: 'export-center' },
					data: { action: 'disconnect-integration', integrationId, companyId },
				})
				emitUnhandledError({
					title: 'Failed to disconnect integration',
					description: 'Please try again or contact support if the issue persists.',
					cause: error,
				})
			})
			.finally(async () => {
				await queryClient.invalidateQueries({ queryKey })
				setIsDisconnecting(false)
			})
	}

	useEffect(() => {
		if ((data?.length ?? 0) > 0) {
			setIsWaitingForWebhook(false)
		}
	}, [data?.length])

	return (
		<ExportCenter
			view={{
				komboStatus: (data?.length ?? 0) > 0 ? 'enabled' : 'disabled',
				komboIntegrationType: data?.[0]?.integrationConfiguration.integrationType ?? '',
				komboToolId: data?.[0]?.integrationConfiguration.komboToolId ?? '',
			}}
			loading={{
				komboStatus: isLoading || isWaitingForWebhook || isDisconnecting,
			}}
			onConnectionComplete={handleConnectionComplete}
			onDisconnect={handleDisconnect}
		/>
	)
}
